import { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily, maxContentWidth, radii, shadows, space, type } from '~/ui/tokens';
import { TopNav, TopNavLogo } from './TopNav';
import { ReferenceSearchBar, type Place, type SearchBarValue } from './ReferenceSearchBar';
import { NavSearchDesktop } from './NavSearchDesktop';

/** The three parts of the results-page summary, shown as distinct groups. */
export interface NavSearchSummary {
  destination: string;
  dates: string;
  guests: string;
}

interface NavSearchBarProps {
  value: SearchBarValue;
  onChange: (value: SearchBarValue) => void;
  onSearch: () => void;
  /** Fired when a destination is chosen from the expanded popular panel. */
  onSelectDestination?: (label: string) => void;
  /** Fired when a destination or hotel is chosen from the search dropdown. */
  onSelectPlace?: (place: Place, search: SearchBarValue) => void;
  /** Home (nudge) label shown inside the pill, e.g. "Find your next stay". */
  triggerLabel: string;
  /** When present, the pill renders the results summary (destination · dates ·
   * guests, with an edit affordance) instead of the nudge label. */
  summary?: NavSearchSummary;
  /** Text shown inside the compact (phone) pill. Defaults to "Search". */
  compactLabel?: string;
  triggerAccessibilityLabel?: string;
  /** Focus the destination field the moment the panel opens (fresh search). On
   * the results page this is off, so an existing destination is never
   * overwritten. */
  autoFocusDestination?: boolean;
  /** Pin the header while the page scrolls (results page). */
  sticky?: boolean;
  back?: boolean;
  autoExpand?: boolean;
}

/**
 * The shared top-nav search control — one component, four states:
 *  01 home collapsed   → nudge pill ("Find your next stay")
 *  02 home expanded    → workspace, destination auto-focused
 *  03 results collapsed→ summary pill (destination · dates · guests, edit)
 *  04 results expanded → same workspace, prefilled, nothing auto-focused
 * The pill sits at the true viewport centre (see TopNav) and the expanded
 * panel drops in beneath the header with desktop side gutters.
 */
export function NavSearchBar({
  value,
  onChange,
  onSearch,
  onSelectDestination,
  onSelectPlace,
  triggerLabel,
  summary,
  compactLabel = 'Search',
  triggerAccessibilityLabel,
  autoFocusDestination = false,
  sticky = false,
  back = false,
  autoExpand = false,
}: NavSearchBarProps) {
  const { width } = useWindowDimensions();
  // Matches TopNav's own compact breakpoint so this panel's mobile chrome
  // (close button, full-width stacked fields) switches at the same width the
  // bar above it does — a mismatch here left the panel looking "desktop"
  // directly under an already-compact nav, or vice versa.
  const stacked = width < 1200;
  // Matches TopNav's inner min-height (flips at the same breakpoint) so the
  // panel drops in directly below the bar with no gap.
  const navHeight = width < 1200 ? 62 : 70;
  const [expanded, setExpanded] = useState(autoExpand);
  const sheetRef = useRef<View>(null);
  const triggerRef = useRef<View>(null);

  // Keyboard: when the phone sheet opens, move focus into it (otherwise Tab
  // keeps walking the page behind the modal), and hand focus back to the search
  // bar when it closes.
  const sheetOpen = expanded && stacked;
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (sheetOpen) {
      const timer = setTimeout(() => {
        const sheet = sheetRef.current as unknown as HTMLElement | null;
        sheet?.querySelector<HTMLElement>('[aria-label^="Destination"], [aria-label="Choose destination"]')?.focus();
      }, 80);
      return () => clearTimeout(timer);
    }
    const trigger = triggerRef.current as unknown as HTMLElement | null;
    if (document.activeElement === document.body) trigger?.focus?.();
  }, [sheetOpen]);

  useEffect(() => {
    if (autoExpand) setExpanded(true);
  }, [autoExpand]);

  useEffect(() => {
    if (!expanded || typeof document === 'undefined') return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [expanded]);

  const accessibilityLabel = triggerAccessibilityLabel ?? `${triggerLabel}. Open search`;

  const pillStyle = ({ pressed }: { pressed: boolean }) => [
    styles.pill,
    expanded && styles.pillActive,
    pressed && styles.pillPressed,
  ];

  const fullTrigger = (
    <NavSearchDesktop
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      onSelectPlace={onSelectPlace}
      autoExpand={autoExpand && !stacked}
      navHeight={navHeight}
    />
  );

  // Phone: a full-width two-line bar under the logo row — destination on top,
  // dates and guests beneath.
  const compactTrigger = (
    <Pressable
      ref={triggerRef}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      onPress={() => setExpanded(true)}
      style={({ pressed }) => [styles.pillCompact, pressed && styles.pillPressed]}
    >
      <Ionicons name="search" size={22} color={colors.brand} />
      <View style={styles.compactCopy}>
        <Text style={styles.compactText} numberOfLines={1}>{summary ? summary.destination : triggerLabel}</Text>
        {summary ? <Text style={styles.compactMeta} numberOfLines={1}>{summary.dates} · {summary.guests}</Text> : null}
      </View>
    </Pressable>
  );

  return (
    <>
      <TopNav back={back} sticky={sticky} center={fullTrigger} compactCenter={compactTrigger} />

      <Modal
        visible={expanded && stacked}
        transparent
        animationType="fade"
        presentationStyle="overFullScreen"
        statusBarTranslucent
        onRequestClose={() => setExpanded(false)}
      >
        {/* Vio-style top sheet: logo row, the four rows, Search — and the page
            below dimmed. Tapping the dim closes it. */}
        <Pressable style={styles.sheetOverlay} onPress={() => setExpanded(false)}>
          <Pressable ref={sheetRef} style={styles.sheet} onPress={(event) => event.stopPropagation()}>
            <View style={styles.sheetHead}>
              <TopNavLogo />
              <Pressable accessibilityRole="button" accessibilityLabel="Close search" onPress={() => setExpanded(false)} style={styles.close}>
                <Ionicons name="close" size={22} color={colors.ink} />
              </Pressable>
            </View>
            <ReferenceSearchBar
              value={value}
              onChange={onChange}
              onSearch={() => { setExpanded(false); onSearch(); }}
              onSelectDestination={onSelectDestination ? (label) => { onChange({ ...value, destination: label, selectedDestination: null }); onSelectDestination(label); } : undefined}
              onSelectPlace={(place, nextSearch) => {
                onChange(nextSearch);
                if (onSelectPlace) {
                  onSelectPlace(place, nextSearch);
                }
              }}
              buttonLabel="Search"
              mobileSheet
              flat
              style={styles.sheetForm}
            />
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  // One pill, shared dimensions across home and results: full-width up to a
  // 560 cap, 48 tall, subtle blue-white fill, neutral blue-grey hairline.
  pill: {
    height: 48,
    width: '100%',
    minWidth: 200,
    maxWidth: 560,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.compact,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.full,
    backgroundColor: colors.cream,
    paddingHorizontal: space.internal,
  },
  // Open state: the pill reads as the workspace's active header, not another
  // input waiting to be filled.
  pillActive: {
    borderColor: colors.brand,
    backgroundColor: colors.card,
  },
  pillPressed: {
    backgroundColor: colors.skyTint,
    borderColor: colors.brand,
  },
  pillCompact: {
    width: '100%',
    minHeight: 56,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.compact,
    paddingHorizontal: space.internal,
    paddingVertical: space.related,
    borderWidth: 2,
    borderColor: colors.brand,
    borderRadius: radii.full,
    backgroundColor: colors.card,
    minWidth: 0,
  },
  compactCopy: {
    flex: 1,
    minWidth: 0,
  },
  nudgeText: {
    flex: 1,
    fontFamily: fontFamily.medium,
    fontSize: type.bodyRegular.fontSize,
    color: colors.inkSoft,
  },
  compactText: {
    fontFamily: fontFamily.semibold,
    fontSize: type.bodyRegular.fontSize,
    color: colors.ink,
  },
  compactMeta: {
    fontFamily: fontFamily.regular,
    fontSize: type.metadata.fontSize,
    color: colors.inkSoft,
  },
  sheetOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
  },
  sheet: {
    width: '100%',
    backgroundColor: colors.card,
    borderBottomLeftRadius: radii.lg,
    borderBottomRightRadius: radii.lg,
    paddingHorizontal: space.card,
    paddingTop: space.related,
    paddingBottom: space.card,
  },
  sheetHead: {
    minHeight: 54,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: space.compact,
  },
  sheetForm: {
    width: '100%',
  },
  // Results summary — destination reads first, dates/guests are secondary, each
  // separated by a hairline so the three facts scan as distinct groups.
  summaryGroups: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.compact,
  },
  summaryDestination: {
    flexShrink: 1,
    fontFamily: fontFamily.semibold,
    fontSize: type.bodyRegular.fontSize,
    color: colors.ink,
  },
  summaryMeta: {
    flexShrink: 0,
    fontFamily: fontFamily.regular,
    fontSize: type.bodyRegular.fontSize,
    color: colors.inkFaint,
  },
  summaryDivider: {
    width: 1,
    height: 16,
    flexShrink: 0,
    backgroundColor: colors.line,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  overlayFull: {
    backgroundColor: colors.card,
  },
  dim: {
    flex: 1,
    // Sits below the panel so a date/guests popover overflowing the panel paints
    // over the dim instead of being covered by it.
    zIndex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
  },
  // The desktop panel keeps ten-percent side gutters; mobile remains full width.
  panel: {
    width: '80%',
    maxWidth: 1040,
    alignSelf: 'center',
    borderBottomLeftRadius: radii.lg,
    borderBottomRightRadius: radii.lg,
    // Above the dim so the date/guests popovers (which overflow the panel) stay
    // in front of the dimmed page beneath.
    zIndex: 2,
    backgroundColor: colors.card,
    borderBottomWidth: 1,
    borderBottomColor: colors.lineSoft,
    paddingHorizontal: space.card,
    paddingTop: space.related,
    paddingBottom: space.component,
    ...shadows.card,
  },
  panelMobile: {
    width: '100%',
    paddingHorizontal: space.internal,
    paddingTop: space.component,
    paddingBottom: space.component,
    flex: 1,
    shadowOpacity: 0,
    elevation: 0,
  },
  panelTitle: {
    flex: 1,
    fontFamily: fontFamily.bold,
    fontSize: type.sectionTitle.fontSize,
    color: colors.ink,
  },
  panelTopline: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: space.component,
  },
  panelForm: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    zIndex: 100,
  },
  close: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.full,
    backgroundColor: colors.lineSoft,
  },
});
